import { formatDate } from '@packages/utils';
import { useGetDocumentContentQuery } from '@ui/features/Patient/services/api';
import { useGetRawDocumentContentQuery } from '@ui/app/api';
import { Alert, Divider, Input, Modal, Spin, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './DocumentViewerModal.module.scss';

const { Title, Text, Paragraph } = Typography;

interface DocumentViewerModalProps {
    open: boolean;
    onClose: () => void;
    patientId?: string;
    noteId?: number;
    documentId?: string; // For Elasticsearch documents
    documentName?: string;
}

export default function DocumentViewerModal({
    open,
    onClose,
    patientId,
    noteId,
    documentId,
    documentName,
}: DocumentViewerModalProps) {
    // Use Elasticsearch query if documentId is provided, otherwise use OMOP query
    const useElasticsearch = !!documentId;

    const {
        currentData: omopDocument,
        isFetching: isFetchingOmop,
        error: omopError,
    } = useGetDocumentContentQuery(
        { patientId: patientId!, noteId: noteId! },
        { skip: !open || !patientId || !noteId || useElasticsearch }
    );

    const {
        data: elasticsearchDocument,
        isFetching: isFetchingElasticsearch,
        error: elasticsearchError,
    } = useGetRawDocumentContentQuery({ documentId: documentId! }, { skip: !open || !documentId || !useElasticsearch });

    // Combine data and loading states
    const documentData = useMemo(() => {
        if (useElasticsearch && elasticsearchDocument) {
            return {
                content: elasticsearchDocument.content || '',
                title: elasticsearchDocument.title || documentName || '',
                documentType: elasticsearchDocument.noteType || '',
                date: elasticsearchDocument.noteDate || '',
            };
        }
        if (!useElasticsearch && omopDocument) {
            return {
                content: omopDocument.content || '',
                title: omopDocument.title || documentName || '',
                documentType: (omopDocument as any).noteType || '',
                date: (omopDocument as any).noteDate || '',
            };
        }
        return null;
    }, [useElasticsearch, elasticsearchDocument, omopDocument, documentName]);

    const isFetching = useElasticsearch ? isFetchingElasticsearch : isFetchingOmop;
    const error = useElasticsearch ? elasticsearchError : omopError;

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const firstHighlightRef = useRef<HTMLElement | null>(null);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset search when modal closes
    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | null = null;

        if (!open) {
            // Defer state resets to avoid synchronous setState within the effect
            timer = setTimeout(() => {
                setSearchTerm('');
                setDebouncedSearchTerm('');
                firstHighlightRef.current = null;
            }, 0);
        }

        return () => {
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [open]);

    // Scroll to first highlight when search term changes
    useEffect(() => {
        if (debouncedSearchTerm.trim() && firstHighlightRef.current && documentData) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                if (firstHighlightRef.current) {
                    const highlightElement = firstHighlightRef.current;
                    const container = highlightElement.closest(`.${styles.documentContentWrapper}`) as HTMLElement;

                    if (container) {
                        // Get positions relative to container
                        const containerRect = container.getBoundingClientRect();
                        const highlightRect = highlightElement.getBoundingClientRect();

                        // Calculate if highlight is visible within container
                        const isVisible =
                            highlightRect.top >= containerRect.top && highlightRect.bottom <= containerRect.bottom;

                        // If not visible, scroll to it within the container
                        if (!isVisible) {
                            // Calculate scroll position
                            const highlightOffsetTop = highlightElement.offsetTop;
                            const containerHeight = container.clientHeight;
                            const highlightHeight = highlightElement.offsetHeight;

                            // Center the highlight in the container
                            const targetScrollTop = highlightOffsetTop - containerHeight / 2 + highlightHeight / 2;

                            container.scrollTo({
                                top: targetScrollTop,
                                behavior: 'smooth',
                            });
                        }
                    }
                }
            }, 100);
        }
    }, [debouncedSearchTerm, documentData]);

    // Highlight text in content
    const highlightText = (text: string, searchTerm: string): React.ReactNode => {
        if (!searchTerm.trim()) {
            return text;
        }

        const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
        const parts = text.split(regex);
        let isFirstMatch = true;

        return parts.map((part, index) => {
            // Check if this part matches the search term (case-insensitive)
            if (part.toLowerCase() === searchTerm.toLowerCase()) {
                const shouldSetRef = isFirstMatch;
                isFirstMatch = false;

                return (
                    <mark
                        key={index}
                        ref={
                            shouldSetRef
                                ? (el: HTMLElement | null) => {
                                      firstHighlightRef.current = el;
                                  }
                                : undefined
                        }
                        style={{ backgroundColor: '#ffeb3b', padding: '2px 0' }}
                    >
                        {part}
                    </mark>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={900}
            centered
            className={styles.documentViewerModal}
            title={
                <div className={styles.documentViewerHeader}>
                    <Title
                        level={4}
                        style={{ margin: 0 }}
                    >
                        {documentName || documentData?.title || 'Document Viewer'}
                    </Title>
                </div>
            }
        >
            {isFetching && (
                <div className={styles.documentViewerLoading}>
                    <Spin size="large" />
                    <Text className="mt-4">Loading document...</Text>
                </div>
            )}

            {error && (
                <Alert
                    message="Error Loading Document"
                    description="Failed to load the document content. Please try again later."
                    type="error"
                    showIcon
                />
            )}

            {documentData && !isFetching && !error && (
                <div className={styles.documentViewerContent}>
                    <div className={styles.documentMetadata}>
                        {documentData.documentType && (
                            <div className={styles.metadataRow}>
                                <Text strong>Document Type:</Text>
                                <Tag color="blue">{documentData.documentType}</Tag>
                            </div>
                        )}
                        <div className={`${styles.metadataRow} ${styles.metadataRowWithSearch}`}>
                            {documentData.date && (
                                <div className={styles.dateSection}>
                                    <Text strong>Date:</Text>
                                    <Text>{formatDate(documentData.date || '')}</Text>
                                </div>
                            )}
                            <Input.Search
                                placeholder="Search in document..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                allowClear
                                size="small"
                                style={{ width: 250 }}
                            />
                        </div>
                    </div>

                    <Divider />

                    <div className={styles.documentContentWrapper}>
                        <Paragraph className={styles.documentText}>
                            {documentData.content
                                ? highlightText(documentData.content, debouncedSearchTerm)
                                : 'No content available'}
                        </Paragraph>
                    </div>
                </div>
            )}
        </Modal>
    );
}
